import { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE_URL } from "../../../config/api";

const REFRESH_URL = `${API_BASE_URL}auth/trader/jwt/refresh/`;

const INITIAL_PAGINATION = {
  page: 0,
  count: 0,
  totalPages: 0,
  pageSize: 0,
};

const parseInteger = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.floor(parsed) : null;
};

const clearTraderSession = () => {
  localStorage.removeItem("access");
  localStorage.removeItem("authCredentials");
  localStorage.removeItem("refresh");
  localStorage.removeItem("role");
  localStorage.removeItem("email");
};

const buildPaginatedUrl = (endpoint, page) => {
  const nextPage = Math.max(1, parseInteger(page) ?? 1);
  const url = new URL(endpoint);
  url.searchParams.set("page", String(nextPage));
  return url.toString();
};

const normalizePaginatedReferralResponse = (data) => {
  const results = Array.isArray(data?.results)
    ? data.results
    : Array.isArray(data)
      ? data
      : [];

  const count = Math.max(0, parseInteger(data?.count) ?? results.length);
  const page = Math.max(1, parseInteger(data?.page) ?? 1);
  const pageSize = Math.max(0, parseInteger(data?.page_size) ?? results.length);
  const totalPagesFromResponse = parseInteger(data?.total_pages);
  const totalPages =
    totalPagesFromResponse !== null
      ? Math.max(totalPagesFromResponse, count > 0 ? 1 : 0, page)
      : pageSize > 0
        ? Math.ceil(count / pageSize)
        : results.length > 0
          ? page
          : 0;

  return {
    page,
    count,
    totalPages,
    pageSize,
    results,
  };
};

const fetchPaginatedReferralPage = async ({ endpoint, page, navigate }) => {
  const requestUrl = buildPaginatedUrl(endpoint, page);

  let response = await fetch(requestUrl, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("access")}`,
    },
  });

  if (response.status === 401) {
    const refreshResponse = await fetch(REFRESH_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        refresh: localStorage.getItem("refresh"),
      }),
    });

    if (!refreshResponse.ok) {
      clearTraderSession();
      navigate("/signin");
      return null;
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);

    response = await fetch(requestUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("access")}`,
      },
    });
  }

  if (!response.ok) {
    throw new Error(`https error! Status: ${response.status}`);
  }

  const data = await response.json();
  return normalizePaginatedReferralResponse(data);
};

export default function usePaginatedReferralList({ endpoint, navigate }) {
  const [items, setItems] = useState([]);
  const [accLoading, setAccLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);
  const [hasNextPage, setHasNextPage] = useState(true);

  const loadMoreRef = useRef(null);
  const isFetchingRef = useRef(false);
  const isMountedRef = useRef(true);
  const paginationRef = useRef(INITIAL_PAGINATION);
  const hasNextPageRef = useRef(true);
  const requestIdRef = useRef(0);

  const loadPage = useCallback(
    async (pageToLoad) => {
      const nextPage = Math.max(1, parseInteger(pageToLoad) ?? 1);
      const isFirstPage = nextPage === 1;

      if (isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;

      if (isFirstPage) {
        setAccLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const data = await fetchPaginatedReferralPage({
          endpoint,
          page: nextPage,
          navigate,
        });

        if (
          !isMountedRef.current ||
          !data ||
          requestId !== requestIdRef.current
        ) {
          return;
        }

        const nextPagination = {
          page: data.page,
          count: data.count,
          totalPages: data.totalPages,
          pageSize: data.pageSize,
        };
        const nextHasNextPage = data.page < data.totalPages;

        setItems((prev) =>
          isFirstPage ? data.results : [...prev, ...data.results],
        );
        setPagination(nextPagination);
        setHasNextPage(nextHasNextPage);
        paginationRef.current = nextPagination;
        hasNextPageRef.current = nextHasNextPage;
      } catch (error) {
        setHasNextPage(false);
        hasNextPageRef.current = false;
        console.error("Error fetching paginated referral data:", error);
      } finally {
        if (isMountedRef.current && requestId === requestIdRef.current) {
          if (isFirstPage) {
            setAccLoading(false);
          } else {
            setIsLoadingMore(false);
          }
        }

        isFetchingRef.current = false;
      }
    },
    [endpoint, navigate],
  );

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      requestIdRef.current += 1;
    };
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    setItems([]);
    setPagination(INITIAL_PAGINATION);
    setHasNextPage(true);
    setAccLoading(true);
    setIsLoadingMore(false);

    paginationRef.current = INITIAL_PAGINATION;
    hasNextPageRef.current = true;
    isFetchingRef.current = false;

    loadPage(1);
  }, [endpoint, navigate, loadPage]);

  useEffect(() => {
    const target = loadMoreRef.current;

    if (!target || accLoading || isLoadingMore || !hasNextPage) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (
          !entry?.isIntersecting ||
          isFetchingRef.current ||
          !hasNextPageRef.current
        ) {
          return;
        }

        const nextPage = paginationRef.current.page + 1;
        loadPage(nextPage);
      },
      {
        rootMargin: "300px 0px",
      },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [accLoading, hasNextPage, isLoadingMore, loadPage]);

  return {
    items,
    accLoading,
    isLoadingMore,
    pagination,
    hasNextPage,
    loadMoreRef,
  };
}
